/**
 * Mandala Mill's geometry: symmetry, the ring maze, and the shapes it stamps.
 *
 * Everything on screen is built from one seed placed in a 30-degree wedge. That wedge
 * is mirrored and rotated six times, which is what makes the output read as a
 * snowflake rather than a pinwheel — six-fold rotation alone gives you a windmill,
 * and it is the reflection that the eye recognises as crystal.
 *
 * The consequence, and the constraint the whole piece rests on: the ring maze has to
 * carry the *same* symmetry. If the filter arcs were laid out freely, the twelve
 * images of a seed would meet different filters, diverge, and the figure would
 * collapse into noise. So a maze is generated once inside the wedge and unfolded.
 */

const TAU = Math.PI * 2;
export const SECTORS = 6;
export const SECTOR = TAU / SECTORS; // 60°
export const WEDGE = SECTOR / 2; // 30°, the fundamental domain

/** Bit-mixing PRNG, so a maze can be replayed from its seed if it ever needs to be. */
const mulberry32 = (seed) => {
  let a = seed >>> 0;
  return () => {
    a += 0x6d2b79f5;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

export const randomSeed = () => Math.floor(Math.random() * 0x7fffffff);

/**
 * Fold any angle into the fundamental wedge.
 *
 * Rotation by 60° and reflection about a sector's centre line both leave the maze
 * unchanged, so every one of the twelve images of a particle folds to the same place
 * and therefore meets the same filter.
 */
export const foldAngle = (a) => {
  let f = a % SECTOR;
  if (f < 0) f += SECTOR;
  return f <= WEDGE ? f : SECTOR - f;
};

/**
 * A thangka palette.
 *
 * Built around the five Buddha-family colours a Tibetan mandala is laid out in —
 * white, yellow, red, green, blue — with the secondary pigments that fill the
 * quadrants and borders: saffron, turquoise, magenta, maroon.
 *
 * These are far more saturated than the set they replace, but they are not the
 * confectionery of the first attempt either. The difference is lightness. Bubblegum
 * is saturated *and* pale, so it glows and flattens; mineral pigment is saturated and
 * *deep*, so it reads as something laid on a surface. Everything here sits between
 * about a quarter and a half lightness for that reason, with the bone white and the
 * black as the only deliberate exceptions.
 */
export const PALETTE = [
  '#b93226', // vermilion
  '#d4691e', // saffron
  '#e0b62e', // golden yellow
  '#8fae3c', // leaf green
  '#1f7a4e', // emerald
  '#37a0ac', // turquoise
  '#22508f', // lapis
  '#5b3a8f', // indigo
  '#a03c6e', // magenta
  '#7d2540', // maroon
  '#f0e8d8', // bone white
  '#0a0c10', // black
];

/** Names for the swatch picker, in PALETTE order. */
export const PALETTE_NAMES = [
  'vermilion',
  'saffron',
  'golden yellow',
  'leaf green',
  'emerald',
  'turquoise',
  'lapis',
  'indigo',
  'magenta',
  'maroon',
  'bone white',
  'black',
];

/**
 * Outline colour per ink, chosen by the fill's own luminance.
 *
 * The outline is black for everything else, which would make a black shape on a black
 * page vanish completely — outline and fill and background all the same. Dark inks
 * get a light edge instead, so a black mark reads as a shape rather than as a hole in
 * the drawing. Relative luminance, not lightness: it is what actually predicts
 * whether an edge will separate from what is behind it.
 */
const luminance = (hex) => {
  const lin = (v) => {
    const c = parseInt(hex.slice(v, v + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(1) + 0.7152 * lin(3) + 0.0722 * lin(5);
};

export const OUTLINE = PALETTE.map((c) =>
  // Gold on the darks and a warm near-black on everything else, rather than plain
  // white and plain black: gilt linework over mineral pigment is what a thangka
  // actually looks like, and a neutral grey edge reads as a print of one.
  luminance(c) < 0.06 ? 'rgba(226,190,104,0.68)' : 'rgba(22,14,7,0.85)'
);

/**
 * Shape descriptors, not bare side counts.
 *
 * A bar, a lozenge and an eight-pointed star are the same polygon family as a square
 * — what separates them is proportion, orientation and whether alternate vertices
 * pull inward. Those become properties, so the drawing code stays one loop:
 *
 *   turn    fixed rotation, so a quad sits square rather than on its point
 *   aspect  flattening across the shape's own axis
 *   star    inner radius as a fraction, for alternating points
 *   scale   overall size against the mark's nominal size
 */
export const SHAPES = [
  { sides: 3, turn: 0, aspect: 1, scale: 1, name: 'triangle' },
  { sides: 4, turn: Math.PI / 4, aspect: 1, scale: 0.95, name: 'square' },
  { sides: 6, turn: 0, aspect: 1, scale: 1, name: 'hexagon' },
  { sides: 8, turn: Math.PI / 8, aspect: 1, scale: 1, name: 'octagon' },
  { sides: 0, turn: 0, aspect: 1, scale: 0.92, name: 'disc' },
  { sides: 4, turn: Math.PI / 4, aspect: 0.26, scale: 1.35, name: 'bar' },
  { sides: 0, turn: 0, aspect: 1, scale: 0.34, name: 'speck' },
  { sides: 4, turn: 0, aspect: 0.6, scale: 1.1, name: 'lozenge' },
  { sides: 0, turn: 0, aspect: 0.45, scale: 1.2, name: 'lens' },
];

export const KIND_COLOUR = {
  split: 'rgba(201,154,78,0.6)',
  recolour: 'rgba(91,143,168,0.55)',
  reshape: 'rgba(139,122,168,0.55)',
  veer: 'rgba(125,154,94,0.55)',
  nest: 'rgba(196,202,214,0.5)',
  edge: 'rgba(181,67,58,0.55)',
};

/**
 * Edge inks a mark can be given explicitly, in the order an `edge` ring cycles them.
 *
 * Distinct from OUTLINE below, which is the automatic choice made from the fill's own
 * luminance. A mark starts on automatic and stays there until an edge ring reaches
 * it; from then on it carries whichever of these it was last given, and so does every
 * concentric ring inside it.
 */
export const EDGE_INKS = ['#f2ede1', '#0a0c10', '#b5433a']; // cream, black, red

/**
 * How far in each concentric outline sits, as a fraction of the mark's own size.
 * Geometric rather than a fixed step, or the innermost ring of a heavily nested mark
 * would end up at a negative radius.
 */
export const NEST_RATIO = 0.66;
export const NEST_MIN = 3.2; // below this a ring is thinner than its own stroke

/**
 * Build a ring maze.
 *
 * Each ring is a set of arcs with gaps cut in them. A particle crossing an arc is
 * transformed; one crossing a gap passes untouched — so where a seed starts decides
 * its entire fate, which is the same causal trick the spirograph plays with its pen
 * offset, and the reason placing a shape is worth doing carefully.
 *
 * Exactly one ring splits. The live population is the *product* across splitting
 * rings, not the sum, and every descendant draws a full arm that is then repeated
 * twelve times — so a second splitting ring does not make the figure busier, it makes
 * it a solid disc.
 */
export const makeMaze = (seed, inner, outer, count = 4) => {
  const rnd = mulberry32(seed);
  const splitRings = new Set([1 + Math.floor(rnd() * (count - 1))]);

  return Array.from({ length: count }, (_, i) => {
    const radius = inner + ((outer - inner) * (i + 0.55)) / count;
    const kind = splitRings.has(i)
      ? 'split'
      : ['recolour', 'reshape', 'veer', 'nest', 'edge'][Math.floor(rnd() * 5)];

    /*
     * One or two gaps, taking between a third and a half of the wedge between them.
     * The old widths topped out near a tenth, which on a 12-fold figure meant almost
     * nothing ever got through a ring untouched — and a maze you cannot thread is not
     * a maze, it is a sequence.
     *
     * Each gap is placed in its own slot so two can never overlap into one wide
     * opening, and neither reaches the wedge edge: a gap flush against the boundary
     * would join with its own mirror image and the ring would stop reading as a
     * barrier at all.
     */
    const gaps = [];
    const n = rnd() < 0.5 ? 2 : 1;
    const total = WEDGE * (0.34 + rnd() * 0.2);
    const widths =
      n === 1 ? [total] : [total * (0.38 + rnd() * 0.24)].map((w) => [w, total - w]).flat();
    const slot = WEDGE / n;
    const edge = WEDGE * 0.05;
    for (let g = 0; g < n; g += 1) {
      const width = Math.min(widths[g], slot - edge * 2);
      const lo = g * slot + edge;
      const hi = (g + 1) * slot - width - edge;
      const from = hi > lo ? lo + rnd() * (hi - lo) : lo;
      gaps.push([from, from + width]);
    }
    gaps.sort((a, b) => a[0] - b[0]);

    // Material is the complement of the gaps, unfolded to absolute angles so the
    // overlay can stroke it without redoing the symmetry every frame.
    const solid = [];
    let cursor = 0;
    gaps.forEach(([a, b]) => {
      if (a > cursor) solid.push([cursor, a]);
      cursor = Math.max(cursor, b);
    });
    if (cursor < WEDGE) solid.push([cursor, WEDGE]);

    const arcs = [];
    solid.forEach(([a, b]) => {
      for (let k = 0; k < SECTORS; k += 1) {
        arcs.push([k * SECTOR + a, k * SECTOR + b]);
        arcs.push([k * SECTOR - b, k * SECTOR - a]);
      }
    });

    return { radius, kind, gaps, arcs, index: i };
  });
};

/** The order a ring cycles through when it is clicked. */
export const KIND_CYCLE = ['split', 'recolour', 'reshape', 'veer', 'nest', 'edge'];

/** Does a particle at this angle meet material, or fall through a gap? */
export const meetsFilter = (ring, angle) => {
  const f = foldAngle(angle);
  return !ring.gaps.some(([a, b]) => f >= a && f <= b);
};

/**
 * Add one shape to a path. Batching many stamps into a single path and filling once
 * is what keeps this affordable: a busy frame lays down a few thousand marks, and as
 * individual fills that is several milliseconds of nothing but state changes.
 */
export const addShape = (path, x, y, size, shape, rot) => {
  const s = size * shape.scale;

  if (!shape.sides) {
    // A moveTo first either way: these paths carry hundreds of marks, and an arc or
    // ellipse appended to a non-empty path draws a line in from wherever it left off.
    if (shape.aspect === 1) {
      path.moveTo(x + s, y);
      path.arc(x, y, s, 0, TAU);
    } else {
      path.moveTo(x + s * Math.cos(rot), y + s * Math.sin(rot));
      path.ellipse(x, y, s, s * shape.aspect, rot, 0, TAU);
    }
    return;
  }

  const cos = Math.cos(rot);
  const sin = Math.sin(rot);
  for (let i = 0; i < shape.sides; i += 1) {
    const a = shape.turn + (i / shape.sides) * TAU;
    const lx = Math.cos(a) * s;
    const ly = Math.sin(a) * s * shape.aspect;
    const px = x + lx * cos - ly * sin;
    const py = y + lx * sin + ly * cos;
    if (i === 0) path.moveTo(px, py);
    else path.lineTo(px, py);
  }
  path.closePath();
};

/**
 * The twelve images of a point: six rotations, each with its reflection.
 * Returned as a flat [x, y, rot, ...] run to avoid allocating per stamp.
 */
export const symmetryImages = (r, angle, out) => {
  for (let k = 0; k < SECTORS; k += 1) {
    const a = angle + k * SECTOR;
    const m = k * SECTOR - angle;
    out[k * 6] = Math.cos(a) * r;
    out[k * 6 + 1] = Math.sin(a) * r;
    out[k * 6 + 2] = a;
    out[k * 6 + 3] = Math.cos(m) * r;
    out[k * 6 + 4] = Math.sin(m) * r;
    out[k * 6 + 5] = m;
  }
  return out;
};
